import { ActionCardsActions } from '@playbooks/action-cards/types'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { ViewMode } from '@playbooks/action-cards-utils'
import { Toaster } from '@playbooks/ui/components/sonner'
import { cn } from '@playbooks/ui/lib/utils'
import { ActionCardsElement } from '@playbooks/action-cards'
import { ActionCard } from '@playbooks/core/src/interfaces/ActionCard'

import SubmitForm from './components/SubmitForm'
import ProgressBar from './components/ProgressBar'
import useMountEffect from './hooks/useMountEffect'
import { ON_ACTION_CARD_SAVE_EVENT } from './constants'
import ActionCardsView from './components/ActionCardsView'
import SliderNavigation from './components/SliderNavigation'
import { FileUploadServiceProvider } from './services/fileUploadService'
import { setTrackFunction, trackRequested } from './services/trackService'
import { parseCompletionKeywords } from './services/completionKeywordsService'
import { saveLocalData } from './services/storage'
import type {
  WebPlaySettings,
  SubmissionService,
  WebPlayConfig,
  ActionCardsDataProvider,
  SliderManager,
  WebPlayLocalizationService,
} from './types'
import { getHighchartSvg } from './services/scoringDimensionsService'
import getAnonymousToken from './utils/getAnonymousToken'
import useEmailControl from './hooks/useEmailControl'
import useCompletionKeywordsMatch from './hooks/useCompletionKeywordsMatch'
import useHorizontalSlider from './hooks/useHorizontalSlider'
import WebPlayContext from './context/WebplayContext'
import { initWeglot } from './services/weglot'
import usePinnedSectionEnabled from './hooks/usePinnedSectionEnabled'

interface WebPlayProps {
  playbookId: string
  storageId?: string
  showScore: boolean
  config: WebPlayConfig
  settings: WebPlaySettings
  actionCard: ActionCard
  submissionService: SubmissionService
  trackFn?: (event: string, data: Record<string, unknown>) => void
  disableInternalToaster?: boolean
}

/**
 * WebPlay component
 *
 * @param {object} props.actionCard - Loaded action card JSON data
 * @param {object} props.settings - WebPlay settings from Playbook
 * @param {object} props.submissionService - Play submission adapter with an optional backend token provider or legacy anonymous-token issuer
 * @param {object} props.config - { serviceBaseUrl, playbooksBackendUrl }
 * @param {string} props.playbookId - Playbook ID for which the WebPlay is rendered
 * @param {string} [props.storageId] - Browser-storage identity when it must differ from the playbook ID
 * @param {string} [props.email] - Default user email if known
 * @param {boolean} [props.showScore] - Whether to show score in the WebPlay
 * @param {function} [props.trackFn] - Optional analytics track function (event, data) => void
 */
export default function WebPlay({
  config,
  trackFn,
  settings,
  showScore,
  actionCard,
  playbookId,
  storageId = playbookId,
  submissionService,
  disableInternalToaster = false,
}: WebPlayProps) {
  const [localization, setLocalization] = useState<{
    apiKey: string
    service: WebPlayLocalizationService
  } | null>(null)

  useEffect(() => {
    if (!settings.weglotApiKey) return

    let isActive = true
    const apiKey = settings.weglotApiKey

    initWeglot(apiKey)
      .then(service => {
        if (isActive) setLocalization({ apiKey, service })
      })
      .catch(error => {
        console.error('Failed to initialize Weglot:', error)
      })

    return () => {
      isActive = false
    }
  }, [settings.weglotApiKey])

  const localizationService =
    localization && localization.apiKey === settings.weglotApiKey ? localization.service : null

  const [progress, setProgress] = useState(0)
  const [isSheetOpen, setIsSheetOpen] = useState(false)
  const [totalScore, setTotalScore] = useState<number>(actionCard.score || 0)
  const hasTrackedRequest = useRef(false)

  const actionCardsData = actionCard
  const webplayContainerRef = useRef<HTMLDivElement>(null)

  const [lastSlideHtml, setLastSlideHtml] = useState<string>(() => {
    // Replace the placeholder with the actual Highchart HTML in the final slide layout on the initial load
    const html =
      settings.finalSlideLayout?.replace('{{scoring-chart}}', getHighchartSvg(actionCardsData, settings.isDarkTheme)) ||
      ''

    return html
  })

  // Initialization of the finish button visibility state based on the enableFunnelView setting
  const [isFinishButtonVisible, setIsFinishButtonVisible] = useState(!settings.enableFunnelView)

  useEffect(() => {
    // If webplay is opened in iframe we send a message to the parent window to show/hide a language switcher
    if (window.self === window.top) return

    const messageValue = isSheetOpen ? 'hide' : isFinishButtonVisible && settings.finalSlideLayout ? 'hide' : 'show'
    window.parent.postMessage({ key: 'languageSwitcherVisibilityState', value: messageValue }, '*')
  }, [isFinishButtonVisible, isSheetOpen, settings.enableFunnelView, settings.finalSlideLayout])

  const [anonymousToken, setAnonymousToken] = useState('')

  const sliderManagerRef = useRef<SliderManager | null>(null)
  const actionCardsElementRef = useRef<ActionCardsElement | null>(null)

  const { enableFunnelView, enableDocumentView, finalSlideLayout } = settings

  const viewMode = enableFunnelView ? ViewMode.FUNNEL : enableDocumentView ? ViewMode.DOC : ViewMode.DEFAULT

  const completionKeywords = useMemo(() => parseCompletionKeywords(settings), [settings])

  const { loggedInUserEmail } = settings

  const {
    isInvalid,
    webPlayEmail,
    isPolicyAccepted,
    isWebPlayEmailValid,
    isWebPlayEmailInputVisible,
    setWebPlayEmail,
    syncWebPlayEmail,
    setIsPolicyAccepted,
  } = useEmailControl({
    storageId,
    loggedInUserEmail,
    actionCardsElementRef,
  })

  const { isCompletionKeywordsMatched, adjustCompletionKeywordsState } = useCompletionKeywordsMatch({
    completionKeywords,
    actionCardsElementRef,
  })

  // Convert webplay settings to action cards settings
  const { actionCardsSettings, actionCardsCallbacks, actionCardsConfig } = useMemo<ActionCardsDataProvider>(() => {
    return {
      actionCardsSettings: {
        viewMode,
        hideLock: true,
        isWebplay: true,
        hideScore: !showScore,
        showMandatoryFields: true,
        hideSeparatorsAbility: true,
        headers: settings.headersCount,
        accentColor: settings.accentColor,
        hideFlow: settings.enableFunnelView,
        lastSlideHtml,
      },
      actionCardsCallbacks: {
        onLastSlideVisible: () => {
          setIsSheetOpen(true)
        },
        // This callback is triggered on every change in the action card,
        // so we update the local data and the state with the latest action card data here
        onChange: async value => {
          const clonedValue = JSON.parse(JSON.stringify(value)) as ActionCard
          // Inserting the spider chart svg into the final slide html if there is the {{scoring-chart}} replacer
          const html =
            finalSlideLayout?.replace('{{scoring-chart}}', getHighchartSvg(clonedValue, settings.isDarkTheme)) || ''
          // Set the final slide html in the component state
          setLastSlideHtml(html)
          // Save action cards data in the actionCardDbStore
          await saveLocalData(clonedValue, storageId)
          // The event is needed to trigger the update of the action cards data in the third-party scripts
          dispatchEvent(new CustomEvent(ON_ACTION_CARD_SAVE_EVENT, { detail: clonedValue }))
          // Synchronize the email in the webplay state with the email in the action card
          syncWebPlayEmail()
          // Check if the completion keywords are matched and update the state accordingly to show/hide the finish button
          adjustCompletionKeywordsState()
          // Update the action cards total score
          setTotalScore(clonedValue.score || 0)
        },
      },
      actionCardsConfig: {
        serviceAuthToken: anonymousToken,
        getBackendToken: submissionService.getBackendToken,
        recommendPlaybooks: async () => [],
        serviceBaseUrl: config.serviceBaseUrl,
        playbooksBackendUrl: config.playbooksBackendUrl,
        fileUploadServiceProvider: new FileUploadServiceProvider(storageId),
      },
    }
  }, [
    syncWebPlayEmail,
    adjustCompletionKeywordsState,
    anonymousToken,
    submissionService.getBackendToken,
    config.playbooksBackendUrl,
    config.serviceBaseUrl,
    finalSlideLayout,
    lastSlideHtml,
    storageId,
    settings.accentColor,
    settings.enableFunnelView,
    settings.headersCount,
    settings.isDarkTheme,
    showScore,
    viewMode,
  ])

  const isSplitViewEnabled = usePinnedSectionEnabled(webplayContainerRef, actionCardsData, viewMode)

  const settingsWithLastSlide = {
    ...actionCardsSettings,
    isSplitViewEnabled,
    lastSlideHtml,
  }

  const handleFinishButtonVisibility = useCallback(
    (isVisible: boolean) => {
      if (settings.enableFunnelView) {
        setIsFinishButtonVisible(isVisible)
      }
    },
    [settings.enableFunnelView],
  )

  const { scrollContainer, step, steps, remapSteps } = useHorizontalSlider({
    playbookId,
    storageId,
    viewMode: settingsWithLastSlide.viewMode,
    sliderManagerRef: sliderManagerRef,
    setIsFinishButtonVisible: handleFinishButtonVisibility,
    actionCardsConfig,
    actionCardsElementRef,
    hasLastSlide: !!settingsWithLastSlide.lastSlideHtml,
    setProgress,
  })

  useMountEffect(() => {
    if (!webplayContainerRef.current) return

    // Make the webplay container visible with a fade-in effect
    // To prevent layout shift, we initially set the opacity to 0 and then fade it in
    webplayContainerRef.current.style.opacity = '1'

    if (!submissionService.getBackendToken && submissionService.createAnonymousToken) {
      // when no backend token is available, create an anonymous token (legacy approach)
      getAnonymousToken(submissionService.createAnonymousToken).then(setAnonymousToken)
    }

    setTrackFunction(trackFn)
    // React Strict Mode reruns mount effects in development; count one request per component lifetime.
    if (trackFn && !hasTrackedRequest.current) {
      hasTrackedRequest.current = true
      trackRequested(playbookId)
    }

    if (!settings.enableFunnelView) return () => setTrackFunction()

    const carouselElements = webplayContainerRef.current?.querySelector('[data-role="webplay-carousel"]')
    const scrollContainer = webplayContainerRef.current?.querySelector(
      '[data-role="data-action-cards-sides-scroll-container"]',
    )

    // Observe '[data-role="data-action-cards-sides-scroll-container"]' scroll width changed to update the progress bar in funnel view
    const mutationObserver = new MutationObserver(() => {
      if (carouselElements) {
        const scrollWidth = carouselElements.scrollWidth - carouselElements.clientWidth
        const currentProgress = (carouselElements.scrollLeft / scrollWidth) * 100

        // Re-map the steps whenever the scroll width changes to ensure accurate step mapping.
        // Needed when there are conditional sections that may change the number of steps dynamically.
        remapSteps()

        setProgress(currentProgress)
      }
    })

    if (scrollContainer) {
      mutationObserver.observe(scrollContainer, { childList: true })
    }

    const handleCarouselScroll = (event: Event) => {
      const target = event.target as HTMLElement

      const scrollWidth = target.scrollWidth - target.clientWidth
      const currentProgress = (target.scrollLeft / scrollWidth) * 100

      setProgress(currentProgress)
    }

    carouselElements?.addEventListener('scroll', handleCarouselScroll)

    return () => {
      setTrackFunction()
      carouselElements?.removeEventListener('scroll', handleCarouselScroll)
      mutationObserver.disconnect()
    }
  })

  const actions: ActionCardsActions = useMemo(() => {
    return {
      switchToNextSlideAutomatically: () => {
        const currentStep = sliderManagerRef.current?.getCurrentStep?.()
        const slides = actionCardsElementRef.current?.getSlidesData?.()

        if (typeof currentStep?.step !== 'number' || !slides) return

        const currentSlide = slides.slides[currentStep.step]

        // Checks that all required fields on the current slide are filled in before moving to the next slide automatically.
        if (!currentSlide?.isFilled) {
          // Highlight the required fields on the current slide
          actionCardsElementRef.current?.highlightElementIds?.(currentSlide?.requiredDataIds || [])

          return
        }

        // Move to the next slide automatically
        sliderManagerRef.current?.nextSlide?.()
      },
    }
  }, [sliderManagerRef])

  // We need a padding at the top if the view mode is not funnel and the split view is enabled
  // to not stick right under the top of the page.
  const shouldBePadding = !settings?.enableFunnelView && isSplitViewEnabled

  return (
    <WebPlayContext value={{ storageId }}>
      <div
        ref={webplayContainerRef}
        data-role="webplay-container"
        className={cn(
          'w-full h-full flex flex-col items-center justify-center opacity-0 transition-opacity duration-150 ease-[cubic-bezier(1,-0.06,.81,.69)]',
          shouldBePadding && 'pt-2',
        )}
      >
        {!disableInternalToaster &&
          createPortal(
            <Toaster position="bottom-center" richColors theme={settings.isDarkTheme ? 'dark' : 'light'} />,
            document.body,
          )}
        {enableFunnelView && (
          <div className="flex w-full px-4 mb-3">
            <ProgressBar progress={progress} accentColor={settings.accentColor} />
          </div>
        )}
        <div className="flex flex-col flex-1 w-full overflow-hidden">
          <ActionCardsView
            actionCard={actionCardsData}
            config={actionCardsConfig}
            settings={settingsWithLastSlide}
            callbacks={actionCardsCallbacks}
            actionCardsElementRef={actionCardsElementRef}
            scrollContainer={scrollContainer}
            actions={actions}
          />
        </div>

        <div
          className={cn(
            'relative z-20 flex flex-col w-full shadow-top px-2',
            settings.enableFunnelView ? 'py-4 gap-y-4 border-t border-border' : 'py-2',
          )}
        >
          <SubmitForm
            viewMode={viewMode}
            isOpen={isSheetOpen}
            showScore={showScore}
            playbookId={playbookId}
            storageId={storageId}
            webplaySettings={settings}
            setIsOpen={setIsSheetOpen}
            totalScore={totalScore}
            submissionService={submissionService}
            completionKeywords={completionKeywords}
            actionCardsElementRef={actionCardsElementRef}
            isInvalid={isInvalid}
            webPlayEmail={webPlayEmail}
            setWebPlayEmail={setWebPlayEmail}
            isPolicyAccepted={isPolicyAccepted}
            setIsPolicyAccepted={setIsPolicyAccepted}
            isWebPlayEmailValid={!!isWebPlayEmailValid}
            isWebPlayEmailInputVisible={isWebPlayEmailInputVisible}
          />

          <SliderNavigation
            viewMode={viewMode}
            accentColor={settings.accentColor}
            openSheet={() => setIsSheetOpen(true)}
            sliderManagerRef={sliderManagerRef}
            actionCardsElementRef={actionCardsElementRef}
            isFinishButtonVisible={isFinishButtonVisible && isCompletionKeywordsMatched}
            step={step}
            stepsCount={steps.length}
            localizationService={localizationService}
            hasLastSlide={!!settingsWithLastSlide.lastSlideHtml}
          />
        </div>
      </div>
    </WebPlayContext>
  )
}
